import routes from 'js/core_pre_webpack/routes';
import { TFunction } from 'i18next';

// TODO: Replace this with i18next.d.ts's LocaleCode and unify the supported languages
export type LanguageCode =
  | 'ar'
  | 'da'
  | 'de'
  | 'el'
  | 'en'
  | 'es'
  | 'fa'
  | 'fi'
  | 'fr'
  | 'he'
  | 'hu'
  | 'id'
  | 'it'
  | 'ko'
  | 'nl'
  | 'no'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'ru'
  | 'sv'
  | 'tr'
  | 'uk'
  | 'zh';

// A mapping of supported languages codes to translated language name
export const getLanguageNames = (
  t: TFunction,
): { [code in LanguageCode]: string } => ({
  ar: t('arabic'),
  da: t('danish'),
  de: t('german'),
  el: t('greek'),
  en: t('english'),
  es: t('spanish'),
  fa: t('persian'),
  fi: t('finnish'),
  fr: t('french'),
  he: t('hebrew'),
  hu: t('hungarian'),
  id: t('indonesian'),
  it: t('italian'),
  ko: t('korean'),
  nl: t('dutch'),
  no: t('norwegian'),
  pl: t('polish'),
  pt: t('portuguese'),
  ro: t('romanian'),
  ru: t('russian'),
  sv: t('swedish'),
  tr: t('turkish'),
  uk: t('ukrainian'),
  zh: t('chinese'),
});

/**
 * Response from the server for translation preview request
 */
export interface PreviewResponse {
  title: string;
  abstract: string;
  abstract_heading: string;
  thumbnail_url: string;
  number_of_pages?: number;
}

// NATIONAL PARKS: Please remove this disable and try to fix it if you're here
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TRANSLATION_IMPRESSION_TRIGGERS = {
  'swp-sticky-header-translate-button': 'sh_t',
  'swp-translate-button': 'swp_t',
  'loswp-translate-button': 'loswp_t',
  'library-translate-button': 'lib_t',
};

export type TranslationTrigger = keyof typeof TRANSLATION_IMPRESSION_TRIGGERS;

// Naviagtes to checkout for translation, with url params set.
// Post-upgrade users are redirected to the work's swp where a
// translation will auto begin.
export function navigateToCheckout(
  trigger: TranslationTrigger,
  workId: number | string,
  premium_discount_coupon?: string,
): void {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'Location'... Remove this comment to see the full error message
  window.location = checkoutUrl(trigger, workId, premium_discount_coupon);
}

// returns the bd checkout url with upgradeRedirect, feature, and trigger set
export function checkoutUrl(
  trigger: TranslationTrigger,
  workId: number | string,
  premium_discount_coupon?: string,
): string {
  // if no filters are selected, select them all post-upgrade so bulk download can start
  return routes.checkout_url({
    feature: 'TRANSLATION',
    trigger: trigger,
    premium_discount_coupon,
    upgradeRedirect: encodeURIComponent(
      routes.show_work_url({ id: workId, translation: true }),
    ),
  });
}

/**
 * Preview translation of a work
 * @param work_id The ID of the work to translate
 * @param source_language Code of the source language
 * @param target_language Code of the target language
 * @returns A promise that resolves to the preview data
 */
export async function getPreview(
  work_id: number,
  source_language: LanguageCode,
  target_language: LanguageCode,
): Promise<PreviewResponse> {
  const response = await fetch(
    routes.api_v0_translation_preview_path({
      subdomain_param: 'api',
      work_id,
      source_language,
      target_language,
    }),
  );
  const json = await response.json();

  return json as PreviewResponse;
}

/**
 * Initiate a download of a translated work
 * @param work_id The ID of the work to translate
 * @param source_language Code of the source language
 * @param target_language Code of the target language
 */
export function downloadTranslation(
  work_id: number,
  source_language: LanguageCode,
  target_language: LanguageCode,
): void {
  // TODO: This can't really handle bad response codes from the translation download API - come up with something better
  const a = document.createElement('a');
  a.href = routes.api_v0_translation_download_url({
    work_id,
    source_language,
    target_language,
  });
  a.click();
}
